import React, { useState, useCallback, useEffect, useMemo, createContext, useContext } from "react";
import { Helmet } from "react-helmet";
import { DragDropContext, Draggable, Droppable, OnDragEndResponder } from "@hello-pangea/dnd";
import { LoaderFunctionArgs, useLoaderData, useNavigate } from "react-router-dom";
import { Box, Button, ButtonGroup, IconButton, Paper, Stack, Typography } from "@mui/material";
import RemoveCircleOutline from "@mui/icons-material/RemoveCircleOutline";

import "./PlanView.css";
import RecipesView from "../components/RecipesView";
import SelectPortions from "../components/base/SelectPortions";
import { datesInRange, dateToString, getMonth, getWeekDay, isSameDay, normalize } from "../dates";
import { ColumnDefinition, RecipeRow } from "../components/RecipesTable";
import { PlanMeal, PlanMeals, Recipe, loadRecipes, planMeals, plannedMeals } from "../data";
import { AuthStatus, useAuthContext } from "../contexts/AuthProvider";
import { RecipeServingTuple } from "../components/ShoppingListView";
import { nextUniqueId } from "../contexts/ShoppingListContext";
import { replace } from "../utils/list";

interface PlannedMealsControl {
    remove(meal: PlanEntry): void;
    changeScaling(meal: PlanEntry, scale: number): void;
}

const EditPlannedMealsContext = createContext<PlannedMealsControl>(null!);
function useEditPlannedMealsContext() {
    return useContext(EditPlannedMealsContext);
}

interface LoaderData {
    dates: Date[];
}

export async function loader({ request }: LoaderFunctionArgs): Promise<LoaderData> {
    const url = new URL(request.url);
    const datesQuery = url.searchParams.get("dates") || "";
    const dateParts = datesQuery.split("_");
    const start = new Date(dateParts[0]);
    const end = new Date(dateParts[1]);
    const dates = datesInRange(start, end);
    return { dates: dates.map(normalize) };
}

interface IPlanDay {
    id: number;
    name: string;
}

interface SelectDayProps {
    days: IPlanDay[];
    disabled?: boolean;
    value?: number;
    onSelectDay?: (day: IPlanDay) => void;
}

function SelectDay({ days, value, onSelectDay, disabled }: SelectDayProps) {
    const onChange = useCallback(
        (e: React.ChangeEvent<HTMLSelectElement>) => {
            if (!onSelectDay) return;

            const valueStr = e.target.value;
            e.target.selectedIndex = 0;
            if (!valueStr) {
                return;
            }
            const value = parseInt(valueStr, 10);
            const day = days.find(d => d.id === value);
            if (day) {
                onSelectDay(day);
            }
        },
        [days, onSelectDay]
    );

    return (
        <select onChange={onChange} value={value ?? 0} disabled={disabled}>
            <option value={0}>Lägg till</option>
            {days.map(d => {
                return (
                    <option key={d.id} value={d.id}>
                        {d.name}
                    </option>
                );
            })}
        </select>
    );
}

function PlannedRecipe({ meal, index }: { meal: PlanEntry; index: number }) {
    const control = useEditPlannedMealsContext();
    const onRemoveMeal = useCallback(() => {
        control.remove(meal);
    }, [control, meal]);

    const onSelectPortion = useCallback(
        (scaling: number) => {
            control.changeScaling(meal, scaling);
        },
        [control, meal]
    );

    return (
        <Draggable draggableId={meal.uniqueId} index={index}>
            {(provided, snapshot) => (
                <Paper
                    ref={provided.innerRef}
                    {...provided.draggableProps}
                    {...provided.dragHandleProps}
                    variant="outlined"
                    sx={{ ml: "5px" }}
                >
                    <Stack direction="row" paddingLeft="15px" alignItems="center" useFlexGap spacing="10px">
                        <span className="plan-day-recipe">{meal.recipe.name}</span>
                        <div className="plan-day-portions">
                            <SelectPortions scaling={meal.scale} onSelectPortion={onSelectPortion}></SelectPortions>
                        </div>
                        <IconButton onClick={onRemoveMeal}>
                            <RemoveCircleOutline />
                        </IconButton>
                    </Stack>
                </Paper>
            )}
        </Draggable>
    );
}

function PlanDay({ date, meals }: { date: Date; meals: PlanEntry[] }) {
    const day = getWeekDay(date);
    const month = getMonth(date);

    return (
        <Droppable droppableId={dateToString(date)}>
            {(provided, snapshot) => (
                <div className="plan-day" {...provided.droppableProps}>
                    <div className="plan-day-header">
                        <div className="plan-day-header-day">{day}</div>
                        <div className="plan-day-header-date">
                            {date.getDate()} {month}
                        </div>
                    </div>

                    <Stack
                        ref={provided.innerRef}
                        className="plan-day-recipes"
                        direction="column"
                        spacing="5px"
                        useFlexGap
                    >
                        {meals.map((m, index) => (
                            <PlannedRecipe key={m.uniqueId} meal={m} index={index}></PlannedRecipe>
                        ))}
                        {provided.placeholder}
                        <div
                            className={
                                "plan-day-empty " +
                                ((meals.length !== 0 && !(meals.length === 1 && snapshot.draggingFromThisWith)) ||
                                snapshot.isDraggingOver
                                    ? "hide"
                                    : "")
                            }
                        >
                            Inget planerat
                        </div>
                    </Stack>
                </div>
            )}
        </Droppable>
    );
}

function isPlannedMealsEqual(a: PlanEntry[], b: PlanEntry[]): boolean {
    if (a === b) return true;
    if (a.length !== b.length) return false;

    for (const planA of a) {
        const planB = b.find(e => e.uniqueId === planA.uniqueId);
        if (!planB) return false;

        if (planA.scale !== planB.scale || !isSameDay(planA.date, planB.date)) return false;
    }

    return true;
}

interface PlanEntry {
    uniqueId: string;
    date: Date;
    recipe: Recipe;
    scale: number;
}

interface PlanViewContentProps {
    householdId: number;
    recipes: RecipeRow<{}>[];
    dates: Date[];
    plannedMeals: PlanEntry[];
    oldPlannedMeals: PlanEntry[];
    setPlannedMeals: React.Dispatch<React.SetStateAction<PlanEntry[]>>;
    setOldPlannedMeals: React.Dispatch<React.SetStateAction<PlanEntry[]>>;
    canEdit?: boolean;
}

function PlanViewContent({
    householdId,
    recipes,
    dates,
    plannedMeals,
    oldPlannedMeals,
    setPlannedMeals,
    setOldPlannedMeals,
    canEdit,
}: PlanViewContentProps) {
    const navigate = useNavigate();

    const hasPendingChanges = !isPlannedMealsEqual(plannedMeals, oldPlannedMeals);
    const planDays: IPlanDay[] = dates.map(d => {
        return { id: d.getTime(), name: `${getWeekDay(d)} ${d.getDate()}/${d.getMonth() + 1}` };
    });

    const control = useMemo<PlannedMealsControl>(
        () => ({
            remove: meal => {
                setPlannedMeals(plannedMeals => {
                    const index = plannedMeals.findIndex(e => e.uniqueId === meal.uniqueId);
                    if (index === -1) {
                        return plannedMeals;
                    }

                    const copy = plannedMeals.slice();
                    copy.splice(index, 1);
                    return copy;
                });
            },

            changeScaling: (meal, scale) => {
                setPlannedMeals(plannedMeals => {
                    const index = plannedMeals.findIndex(e => e.uniqueId === meal.uniqueId);
                    if (index === -1) {
                        return plannedMeals;
                    }

                    const copy = plannedMeals.slice();
                    copy.splice(index, 1);
                    return replace(plannedMeals, index, { ...plannedMeals[index], scale });
                });
            },
        }),
        [setPlannedMeals]
    );

    const onSave = async () => {
        let request: PlanMeals[] = [];
        for (const date of dates) {
            const planned = plannedMeals
                .filter(e => e.date === date)
                .map<PlanMeal>(e => ({ recipe_id: e.recipe.id, servings: e.scale }));

            request.push({ date, planned });
        }

        try {
            await planMeals(householdId, request);
            setOldPlannedMeals(plannedMeals);
        } catch (e) {
            console.error("Planned meals failed", e);
        }
    };

    const onSelectDay = useCallback(
        (recipe: RecipeRow<{}>, day: IPlanDay) => {
            const date = dates.find(d => d.getTime() === day.id);
            if (date) {
                setPlannedMeals(plannedMeals =>
                    plannedMeals.concat([{ uniqueId: nextUniqueId(), recipe, date, scale: recipe.servings ?? 1 }])
                );
            }
        },
        [dates, setPlannedMeals]
    );

    const onDragEnd: OnDragEndResponder = result => {
        const { source, destination } = result;
        if (!destination) {
            return;
        }

        const fromDate = new Date(source.droppableId);
        const toDate = new Date(destination.droppableId);
        if (source.droppableId === destination.droppableId) {
            return;
        }

        const sourceDate = dates.find(d => isSameDay(d, fromDate));
        const targetDate = dates.find(d => isSameDay(d, toDate));
        if (!targetDate) return;

        const recipes = plannedMeals.filter(e => e.date === sourceDate);

        const uniqueId = recipes[source.index]?.uniqueId;

        setPlannedMeals(plannedMeals => {
            const index = plannedMeals.findIndex(e => e.uniqueId === uniqueId);
            if (index === -1) {
                return plannedMeals;
            }

            return replace(plannedMeals, index, { ...plannedMeals[index], date: targetDate });
        });
    };

    const onShowShoppingList = () => {
        let recipesToShop: RecipeServingTuple[] = [];
        for (const entry of plannedMeals.values()) {
            recipesToShop.push([entry.recipe.id, entry.date.getTime(), entry.scale]);
        }

        const meals = encodeURIComponent(JSON.stringify(recipesToShop.sort((a, b) => a[1] - b[1])));
        navigate(`/shopping-list?meals=${meals}`);
    };

    const extraColumns: ColumnDefinition<RecipeRow<{}>>[] = useMemo(
        () => [
            {
                name: "Dag",
                build: recipe => (
                    <SelectDay
                        days={planDays}
                        onSelectDay={day => onSelectDay(recipe, day)}
                        disabled={!canEdit}
                    ></SelectDay>
                ),
                sort: (_a, _b) => 0,
            },
        ],
        [planDays, canEdit, onSelectDay]
    );

    return (
        <EditPlannedMealsContext.Provider value={control}>
            <Stack
                direction="row"
                sx={{
                    minHeight: "100%",
                    flexGrow: "1",
                    position: "relative",
                }}
            >
                <Helmet>
                    <title>Planera</title>
                </Helmet>
                <Box
                    sx={{
                        position: "absolute",
                        height: "100%",
                        left: 0,
                        overflowY: "auto",
                        width: "395px",
                    }}
                >
                    <Stack direction="column" padding="15px">
                        <Typography variant="h6" marginBottom="15px">
                            Att planera
                        </Typography>
                        <DragDropContext onDragEnd={onDragEnd}>
                            <Stack direction="column">
                                {dates.map(d => {
                                    const meals = plannedMeals.filter(e => e.date === d);
                                    return <PlanDay key={d.toISOString()} date={d} meals={meals}></PlanDay>;
                                })}
                            </Stack>
                        </DragDropContext>
                        <Stack direction="row">
                            <ButtonGroup variant="contained">
                                <Button onClick={onSave} disabled={!canEdit || !hasPendingChanges}>
                                    Spara
                                </Button>
                                <Button disabled={hasPendingChanges} onClick={onShowShoppingList}>
                                    Inköpslista
                                </Button>
                            </ButtonGroup>
                        </Stack>
                    </Stack>
                </Box>
                <Box
                    sx={{
                        position: "absolute",
                        height: "100%",
                        right: 0,
                        overflowY: "scroll",
                        width: "calc(100% - 400px)",
                    }}
                >
                    <Box paddingBottom="30px">
                        <RecipesView recipes={recipes} extraColumns={extraColumns}></RecipesView>
                    </Box>
                </Box>
            </Stack>
        </EditPlannedMealsContext.Provider>
    );
}

function PlanView() {
    const { dates } = useLoaderData() as LoaderData;
    const auth = useAuthContext();
    const currentHouseholdId = auth.status === AuthStatus.SignedIn ? auth.currentHousehold : undefined;
    const householdPermission =
        auth.status === AuthStatus.SignedIn
            ? auth.user.households.find(h => h.id === currentHouseholdId)?.relation
            : undefined;
    const canEdit = householdPermission === "Owner" || householdPermission === "Writer";

    const [recipes, setRecipes] = useState<Recipe[] | undefined>();
    const [plannedMealsMap, setPlannedMealsMap] = useState<PlanEntry[]>();
    const [oldPlannedMealsMap, setOldPlannedMealsMap] = useState<PlanEntry[]>();

    useEffect(() => {
        let cleanedUp = false;
        Promise.all([
            loadRecipes(currentHouseholdId),
            plannedMeals(currentHouseholdId, dates[0], dates[dates.length - 1]),
        ]).then(([recipes, plannedMeals]) => {
            if (cleanedUp) return;

            const entries: PlanEntry[] = [];
            for (const meal of plannedMeals) {
                const mealDate = normalize(new Date(meal.date));
                const foundDate = dates.find(d => isSameDay(d, mealDate));
                if (foundDate) {
                    entries.push({
                        uniqueId: nextUniqueId(),
                        date: foundDate,
                        recipe: meal.recipe,
                        scale: meal.servings ?? 1,
                    });
                }
            }
            setRecipes(recipes);
            setPlannedMealsMap(entries);
            setOldPlannedMealsMap(entries);
        });

        return () => {
            cleanedUp = true;
        };
    }, [currentHouseholdId, dates]);

    if (!recipes || !plannedMealsMap || currentHouseholdId === undefined) {
        return <div>Loading...</div>;
    }

    return (
        <PlanViewContent
            householdId={currentHouseholdId}
            dates={dates}
            recipes={recipes}
            plannedMeals={plannedMealsMap}
            oldPlannedMeals={oldPlannedMealsMap!}
            setPlannedMeals={setPlannedMealsMap as any}
            setOldPlannedMeals={setOldPlannedMealsMap as any}
            canEdit={canEdit}
        ></PlanViewContent>
    );
}

export default PlanView;
